import { Product, useFetchProductsQuery } from "../../Products/api/productApi";
import React from "react";
import { Card, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { useController, UseControllerProps, useFormContext, useWatch } from "react-hook-form";
import {
  numberInputClasses,
  NumberInputProps,
  Unstable_NumberInput as BaseNumberInput,
} from "@mui/base/Unstable_NumberInput";
import { styled } from "@mui/system";
import _ from "lodash";

type DefaultCounts = {
  product_ids: {
    [product_id: number]: { count: number };
  };
};

export default function ProductsInCategories({ categories }: { categories: { id: number }[] }) {
  const skip = categories[0]?.id == null;
  const { data } = useFetchProductsQuery(
    {
      gridFilterItems: {
        category_id: { field: "category_id", operator: "=", value: categories[0]?.id ?? 0 },
      },
    },
    { skip }
  );

  if (skip) {
    return null;
  }
  return data == null ? <CircularProgress /> : <ProductDefaultCounts products={data?.data ?? []} />;
}
export function ProductsList({ products }: { products: { id: number }[] }) {
  const skip = products[0]?.id == null;
  const { data } = useFetchProductsQuery(
    {
      gridFilterItems: {
        product_id: { field: "product_id", operator: "=", value: _.map(products, (product) => product.id) ?? [] },
      },
    },
    { skip }
  );

  if (skip) {
    return null;
  }
  return data == null ? <CircularProgress /> : <ProductDefaultCounts products={data?.data ?? []} />;
}

function ProductDefaultCounts({ products }: { products: Product[] }) {
  const { control } = useFormContext<{ default_counts: DefaultCounts | null }>();
  const useControllerProps: UseControllerProps<{ default_counts: DefaultCounts | null }> = {
    control,
    name: "default_counts",
    defaultValue: { product_ids: {} },
  };
  const {
    field: { value, onChange },
  } = useController(useControllerProps);

  console.log(value);
  const countable = useWatch({ name: "countable" });
  return (
    <Card>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Option name</TableCell>
            <TableCell>Default count</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...products]
            .sort((a, b) => (a.sortkey ?? 0) - (b.sortkey ?? 0))
            .map((product) => (
              <ProductListItem
                defaultCount={(value as DefaultCounts).product_ids[product.id]?.count ?? undefined}
                key={product.id}
                maxCount={!countable ? 1 : undefined}
                minCount={0}
                onChange={(newValue) => {
                  if (value.product_ids.length === 0) {
                    value.product_ids = {};
                  }
                  (value as DefaultCounts).product_ids[product.id] = { count: newValue };
                  console.log(value);
                  onChange(value);
                }}
                product={product}
              />
            ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function ProductListItem({
  product,
  defaultCount,
  onChange,
  minCount,
  maxCount,
}: {
  product: Product;
  defaultCount?: number;
  onChange: (value: number) => void;
  minCount?: number;
  maxCount?: number;
}) {
  // const [textValue, setTextValue] = useState(String(defaultCount ?? 0));
  return (
    <TableRow>
      <TableCell>{product.naam}</TableCell>
      <TableCell>
        <CustomNumberInput
          max={maxCount}
          min={minCount}
          onChange={(e, value) => {
            // setTextValue(value);
            const number = Number(value);
            if (!Number.isNaN(number)) {
              onChange(number);
            }
          }}
          style={{ width: 100 }}
          value={defaultCount ?? 0}
        />
      </TableCell>
    </TableRow>
  );
}

const CustomNumberInput = React.forwardRef(function CustomNumberInput(
  props: NumberInputProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  return (
    <BaseNumberInput
      slotProps={{
        incrementButton: {
          children: "▴",
        },
        decrementButton: {
          children: "▾",
        },
      }}
      slots={{
        root: StyledInputRoot,
        input: StyledInputElement,
        incrementButton: StyledButton,
        decrementButton: StyledButton,
      }}
      {...props}
      ref={ref}
    />
  );
});

// export  function NumberInputIntroduction() {
//   return <CustomNumberInput aria-label="Demo number input" placeholder="Type a number…" />;
// }

// const blue = {
//   100: "#DAECFF",
//   200: "#80BFFF",
//   400: "#3399FF",
//   500: "#007FFF",
//   600: "#0072E5",
//   700: "#0059B2",
// };

const grey = {
  50: "#F3F6F9",
  100: "#E5EAF2",
  200: "#DAE2ED",
  300: "#C7D0DD",
  400: "#B0B8C4",
  500: "#9DA8B7",
  600: "#6B7A90",
  700: "#434D5B",
  800: "#303740",
  900: "#1C2025",
};

const StyledInputRoot = styled("div")(
  ({ theme }) => `
  font-family: 'IBM Plex Sans', sans-serif;
  font-weight: 400;
  border-radius: 8px;
  color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
  background: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
  border: 1px solid ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
  box-shadow: 0px 2px 4px ${theme.palette.mode === "dark" ? "rgba(0,0,0, 0.5)" : "rgba(0,0,0, 0.05)"};
  display: grid;
  grid-template-columns: 1fr 19px;
  grid-template-rows: 1fr 1fr;
  overflow: hidden;
  column-gap: 8px;
  padding: 4px;

  &.${numberInputClasses.focused} {
    border-color: ${theme.palette.primary.main};
    box-shadow: 0 0 0 3px ${theme.palette.mode === "dark" ? theme.palette.primary.dark : theme.palette.primary.light};
  }

  &:hover {
    border-color: ${theme.palette.primary.main};
  }

  // firefox
  &:focus-visible {
    outline: 0;
  }
`
);

const StyledInputElement = styled("input")(
  ({ theme }) => `
  font-size: 0.875rem;
  font-family: inherit;
  font-weight: 400;
  line-height: 1.5;
  grid-column: 1/2;
  grid-row: 1/3;
  color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
  background: inherit;
  border: none;
  border-radius: inherit;
  padding: 8px 12px;
  outline: 0;
`
);

const StyledButton = styled("button")(
  ({ theme }) => `
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  appearance: none;
  padding: 0;
  width: 19px;
  height: 19px;
  font-family: system-ui, sans-serif;
  font-size: 0.875rem;
  line-height: 1;
  box-sizing: border-box;
  background: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
  border: 0;
  color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 120ms;

  &:hover {
    background: ${theme.palette.mode === "dark" ? grey[800] : grey[50]};
    border-color: ${theme.palette.mode === "dark" ? grey[600] : grey[300]};
    cursor: pointer;
  }

  &.${numberInputClasses.incrementButton} {
    grid-column: 2/3;
    grid-row: 1/2;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    border: 1px solid;
    border-bottom: 0;
    border-color: ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
    background: ${theme.palette.mode === "dark" ? grey[900] : grey[50]};
    color: ${theme.palette.mode === "dark" ? grey[200] : grey[900]};

    &:hover {
      cursor: pointer;
      color: #FFF;
      background: ${theme.palette.mode === "dark" ? theme.palette.primary.dark : theme.palette.primary.dark};
      border-color: ${theme.palette.mode === "dark" ? theme.palette.primary.main : theme.palette.primary.dark};
    }
  }

  &.${numberInputClasses.decrementButton} {
    grid-column: 2/3;
    grid-row: 2/3;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    border: 1px solid;
    border-color: ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
    background: ${theme.palette.mode === "dark" ? grey[900] : grey[50]};
    color: ${theme.palette.mode === "dark" ? grey[200] : grey[900]};
  }

  &:hover {
    cursor: pointer;
    color: #FFF;
    background: ${theme.palette.mode === "dark" ? theme.palette.primary.dark : theme.palette.primary.dark};
    border-color: ${theme.palette.mode === "dark" ? theme.palette.primary.main : theme.palette.primary.dark};
  }

  & .arrow {
    transform: translateY(-1px);
  }

  & .arrow {
    transform: translateY(-1px);
  }
`
);
